import { determinize } from '../ai';
import {
  Pcg32,
  cardPlayability,
  epitaphOptions,
  legalActions,
  previewLink,
  type Action,
  type EngineError,
  type EpitaphOption,
  type LinkPreview,
  type MatchEvent,
  type MatchState,
  type MatchView,
  type Playability,
  type PlayLinkAction,
  type Replay,
  type Side,
} from '../engine';
import {
  PROTOCOL_VERSION,
  type ClientMessage,
  type Role,
  type RoomInfo,
  type ServerMessage,
} from '../online/protocol';
import {
  draftFrom,
  type Controller,
  type Draft,
  type GameHost,
  type HostSnapshot,
} from './matchHost';
import { roomSocketUrl, type OnlineIdentity } from './online';

/** How the room stands, for the room screen (before, around and after the match). */
export interface RoomState {
  readonly version: number;
  readonly connection: 'connecting' | 'open' | 'reconnecting';
  /** The room did not take the player's name (not allowed, or another player's). */
  readonly nameRefused: 'bad-name' | 'name-taken' | null;
  readonly role: Role | null;
  readonly room: RoomInfo | null;
  /** The match has started and a view has arrived: the match screen can be shown. */
  readonly hasView: boolean;
  /** The whole match, once it is over. */
  readonly replay: Replay | null;
  readonly rematch: { readonly id: string; readonly by: Side } | null;
  readonly error: string | null;
}

export interface RemoteHostOptions {
  /** Opens the socket (tests pass a fake). */
  readonly socket?: (url: string) => WebSocket;
  readonly url?: string;
  /** Delay before the n-th reconnection attempt, in ms. */
  readonly backoff?: (attempt: number) => number;
}

const OPEN = 1;
/**
 * How often the connection is checked: a ping goes out, and the room answers every one, so a
 * connection that has brought nothing at all by the next ping is gone, even one the browser still
 * says is open (a network that went away without a word).
 */
const PING_MS = 25_000;
/** The room answers a move at once: one still unanswered after this long means the same. */
const ANSWER_MS = 15_000;

/**
 * The match host of an online game: the match runs in a room on the server (workers/rooms), and
 * this host shows the view the room sends and forwards this player's actions. Legal moves and
 * previews come from a state built from the view alone (determinize), which is exact for the
 * player's own options: they depend only on public cards and the player's own hand. The
 * connection comes back by itself after a drop.
 */
export class RemoteHost implements GameHost {
  private ws: WebSocket | null = null;
  private readonly listeners = new Set<() => void>();
  private disposed = false;
  private attempts = 0;
  /** The room has welcomed the current connection (answered its greeting). */
  private welcomed = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private answerTimer: ReturnType<typeof setTimeout> | null = null;
  /** Something came from the room since the last ping. */
  private heard = true;

  private view: MatchView | null = null;
  private state: MatchState | null = null;
  private log: MatchEvent[] = [];
  private lastEvents: MatchEvent[] = [];
  private lastActor: Side | null = null;
  private seq = 0;
  private pending = false;
  /** The rest of a turn being sent: each goes once the table has applied the one before. */
  private queue: Action[] = [];
  private legalCache: { seq: number; side: Side; actions: Action[] } | null = null;

  private connection: RoomState['connection'] = 'connecting';
  private nameRefused: RoomState['nameRefused'] = null;
  private role: Role | null = null;
  private room: RoomInfo | null = null;
  private replay: Replay | null = null;
  private rematch: RoomState['rematch'] = null;
  private error: string | null = null;
  private version = 0;
  private roomState: RoomState;
  private snapshot: HostSnapshot | null = null;

  constructor(
    readonly roomId: string,
    private readonly identity: OnlineIdentity,
    private readonly options: RemoteHostOptions = {},
  ) {
    this.roomState = this.buildRoomState();
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getRoom = (): RoomState => this.roomState;

  /** The match snapshot; only defined once RoomState.hasView is true. */
  getSnapshot = (): HostSnapshot => {
    if (!this.snapshot) throw new Error('the match has not started');
    return this.snapshot;
  };

  /** Opens the connection (again, after close()). */
  connect(): void {
    this.disposed = false;
    if (this.ws) return;
    const open = this.options.socket ?? ((url: string) => new WebSocket(url));
    let ws: WebSocket;
    try {
      ws = open(this.options.url ?? roomSocketUrl(this.roomId));
    } catch {
      this.retry();
      return;
    }
    this.ws = ws;
    this.welcomed = false;
    ws.addEventListener('open', () => {
      if (this.ws !== ws) return;
      this.connection = 'open';
      this.send({
        t: 'hello',
        v: PROTOCOL_VERSION,
        player: this.identity.player,
        key: this.identity.key,
        name: this.identity.name,
      });
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.heard = true;
      this.pingTimer = setInterval(() => {
        if (this.ws !== ws) return;
        if (!this.heard) return this.drop(ws);
        this.heard = false;
        this.send({ t: 'ping' });
      }, PING_MS);
      this.publish();
    });
    ws.addEventListener('message', (e) => {
      if (this.ws !== ws) return;
      this.heard = true;
      this.receive(String((e as MessageEvent).data));
    });
    ws.addEventListener('close', () => this.lost(ws));
  }

  /**
   * The connection is gone: what was being sent is let go (a turn cut off by a lost connection is
   * not resumed: the fresh state on reconnecting shows what landed), and a new one is opened.
   */
  private lost(ws: WebSocket): void {
    if (this.ws !== ws) return;
    this.ws = null;
    this.pending = false;
    this.queue = [];
    this.stopTimers();
    this.retry();
  }

  /** Gives up on a connection that has gone silent, and opens a new one. */
  private drop(ws: WebSocket): void {
    this.lost(ws);
    try {
      ws.close(4000, 'silent');
    } catch {
      // Already closing.
    }
  }

  private stopTimers(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (this.answerTimer) clearTimeout(this.answerTimer);
    this.pingTimer = null;
    this.answerTimer = null;
  }

  /** Sends a move: the room answers it at once, or the connection is taken for gone. */
  private sendMove(action: Action): boolean {
    const ws = this.ws;
    if (!ws || !this.send({ t: 'act', seq: this.seq, action })) return false;
    if (this.answerTimer) clearTimeout(this.answerTimer);
    this.answerTimer = setTimeout(() => {
      this.answerTimer = null;
      if (this.pending) this.drop(ws);
    }, ANSWER_MS);
    return true;
  }

  private retry(): void {
    if (this.disposed) return;
    this.connection = 'reconnecting';
    const wait =
      this.options.backoff?.(this.attempts) ?? Math.min(15_000, 1000 * 2 ** this.attempts);
    this.attempts++;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.connect();
    }, wait);
    this.publish();
  }

  private send(msg: ClientMessage): boolean {
    if (!this.ws || this.ws.readyState !== OPEN) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  private receive(raw: string): void {
    let msg: ServerMessage;
    try {
      msg = JSON.parse(raw) as ServerMessage;
    } catch {
      return;
    }
    switch (msg.t) {
      case 'welcome':
        // Only a connection the room has taken counts: one turned away tries again later.
        this.attempts = 0;
        this.welcomed = true;
        this.role = msg.role;
        this.room = msg.room;
        break;
      case 'state':
        this.log = msg.log ? [...msg.log] : [...this.log, ...msg.events];
        this.view = msg.view;
        this.seq = msg.seq;
        this.lastEvents = [...msg.events];
        this.lastActor = msg.lastActor;
        this.state = determinize(msg.view, Pcg32.seeded(1n, 1n));
        this.legalCache = null;
        this.pending = false;
        this.error = null;
        if (this.queue.length > 0 && msg.lastActor === this.role) {
          const next = this.queue.shift() as Action;
          if (this.sendMove(next)) this.pending = true;
          else this.queue = [];
        }
        break;
      case 'room':
        this.room = msg.room;
        break;
      case 'end':
        this.replay = msg.replay;
        this.room = msg.room;
        break;
      case 'rematch':
        this.rematch = { id: msg.id, by: msg.by };
        break;
      case 'error':
        if (msg.code === 'bad-name' || msg.code === 'name-taken') this.nameRefused = msg.code;
        this.error = msg.message;
        this.pending = false;
        this.queue = [];
        // The table could not take this player just now (its database is failing): the greeting
        // is made again on a new connection, a little later each time.
        if (msg.code === 'unavailable' && !this.welcomed && this.ws) {
          this.drop(this.ws);
          return;
        }
        break;
      case 'pong':
        return;
    }
    this.publish();
  }

  isHuman(side: Side): boolean {
    return this.role === side;
  }

  legal(side: Side): Action[] {
    if (!this.state || side !== this.role) return [];
    const c = this.legalCache;
    if (c && c.seq === this.seq && c.side === side) return c.actions;
    const actions = legalActions(this.state, side);
    this.legalCache = { seq: this.seq, side, actions };
    return actions;
  }

  preview(action: PlayLinkAction): LinkPreview | { error: EngineError } {
    if (!this.state) return { error: { code: 'NOT_YOUR_DECISION', message: 'not started' } };
    return previewLink(this.state, action);
  }

  playability(side: Side, cardId: string): Playability {
    return this.state
      ? cardPlayability(this.state, side, cardId)
      : { playable: false, reason: 'NOT_YOUR_TURN' };
  }

  epitaphOptions(side: Side): EpitaphOption[] {
    return this.state ? epitaphOptions(this.state, side) : [];
  }

  draft(actions: readonly Action[]): Draft | { error: EngineError } {
    if (!this.state || (this.role !== 'S' && this.role !== 'N'))
      return { error: { code: 'NOT_YOUR_DECISION', message: 'you are watching this game' } };
    return draftFrom(this.state, this.role, actions);
  }

  submit(action: Action): EngineError | null {
    if (this.role !== 'S' && this.role !== 'N')
      return { code: 'NOT_YOUR_DECISION', message: 'you are watching this game' };
    if (this.pending) return { code: 'NOT_YOUR_DECISION', message: 'waiting for the table' };
    if (!this.sendMove(action))
      return { code: 'NOT_YOUR_DECISION', message: 'not connected: reconnecting' };
    this.pending = true;
    // A refusal of an earlier action no longer stands.
    this.error = null;
    return null;
  }

  submitAll(actions: readonly Action[]): EngineError | null {
    if (this.queue.length > 0)
      return { code: 'NOT_YOUR_DECISION', message: 'waiting for the table' };
    const [first, ...rest] = actions;
    if (!first) return null;
    const err = this.submit(first);
    if (err) return err;
    this.queue = rest;
    this.publish();
    return null;
  }

  /** Ends the game in this player's favour when the opponent has been away long enough. */
  claim(): void {
    this.send({ t: 'claim' });
  }

  askRematch(): void {
    this.send({ t: 'rematch' });
  }

  setPaused(): void {
    // The room decides the pace: nothing to hold.
  }

  acknowledgeHandoff(): void {
    // Each player has a device of their own.
  }

  /** Leaves the room: closes the connection and stops reconnecting, until connect() again. */
  close(): void {
    this.disposed = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.stopTimers();
    const ws = this.ws;
    this.ws = null;
    try {
      ws?.close(1000, 'left');
    } catch {
      // Already closed.
    }
  }

  private controllers(): Record<Side, Controller> {
    const seat = (side: Side): Controller => ({
      kind: 'human',
      name: this.room?.seats[side]?.name ?? (side === 'S' ? 'South' : 'North'),
    });
    return { S: seat('S'), N: seat('N') };
  }

  private buildRoomState(): RoomState {
    return {
      version: this.version,
      connection: this.connection,
      nameRefused: this.nameRefused,
      role: this.role,
      room: this.room,
      hasView: this.view !== null,
      replay: this.replay,
      rematch: this.rematch,
      error: this.error,
    };
  }

  private publish(): void {
    this.version++;
    this.roomState = this.buildRoomState();
    const v = this.view;
    if (v) {
      const viewer: Side = this.role === 'S' || this.role === 'N' ? this.role : 'S';
      const playing = v.phase !== 'MATCH_OVER';
      this.snapshot = {
        version: this.version,
        actionCount: this.seq,
        viewer,
        view: v,
        controllers: this.controllers(),
        log: this.log,
        lastEvents: this.lastEvents,
        lastActor: this.lastActor,
        thinking: playing && v.decider !== this.role ? v.decider : null,
        handoff: null,
        error: this.error,
        busy: this.pending || this.queue.length > 0,
      };
    }
    for (const l of this.listeners) l();
  }
}
